import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_URL = "https://readdy.ai/api/form/dbb1eea9ke8mc8ad7ce0";

type ApiResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const scopeList = [
  {
    icon: "ri-book-open-line",
    title: "剧情介绍与资料整理",
    desc: "每部影片都配有结构化剧情简介、导演与主演信息、年份与类型标签，方便快速判断是否符合口味。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "分类与筛选",
    desc: "按经典、最新、导演专题、题材等维度组织片单，支持年份与排序组合筛选，结果即时更新。",
  },
  {
    icon: "ri-search-line",
    title: "关键词检索",
    desc: "支持片名、导演、主演与剧情关键词的模糊匹配，帮助你在片单里快速定位某部作品。",
  },
];

const limits = [
  "不提供在线播放、下载或任何形式的资源分发。",
  "不提供会员、充值、支付与交易功能。",
  "不收集账号信息，无需注册即可浏览全部内容。",
  "所有片名、剧情、导演与演员均为虚构示例，与真实作品无关。",
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [count, setCount] = useState(0);

  useDocumentMeta(
    "关于我们 - 三级片影视大全",
    "了解三级片影视大全的站点定位、内容范围与使用提示。本站专注影片资料与剧情介绍，不提供播放与下载，内容均为虚构示例。",
    "关于三级片,三级片影视大全,三级片网站介绍,三级片内容说明"
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们会认真阅读每一条建议。");
      form.reset();
      setCount(0);
      return;
    }

    data.delete("website_alt");

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: ApiResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ApiResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || "";
      const isSpam = serverMessage.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFeedback(serverMessage || "提交没有成功，请稍后再试一次。");
        return;
      }

      setStatus("success");
      setFeedback("反馈已提交，感谢你的建议。");
      form.reset();
      setCount(0);
    } catch {
      setStatus("error");
      setFeedback("网络似乎不太稳定，请稍后再试一次。");
    }
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-300 bg-background-100 pt-16 md:pt-[72px]">
        <div className="mx-auto w-full max-w-[1440px] px-4 pb-12 pt-10 md:px-8 md:pb-16 md:pt-14 lg:px-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <span className="transition-colors">首页</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">About the archive</p>
              <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[44px] md:leading-[1.15]">
                关于三级片影视大全
              </h1>
              <p className="mt-5 max-w-2xl font-body text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                三级片影视大全是一个专注影片资料与剧情介绍的影视索引站，面向对三级片感兴趣的成年观众。
                我们不做播放器，也不做资源站，只把「这部片讲了什么、谁拍的、和哪些作品属于同一条线索」讲清楚，
                让你在决定观看之前先完成一次完整的了解。
              </p>
              <p className="mt-4 max-w-2xl font-body text-sm leading-[1.9] text-foreground-600 md:text-[15px]">
                站内的片单按年代与题材整理，既有八九十年代的经典作品，也有近五年的新作。
                每部影片都配有海报、基本信息、分段剧情简介与同类推荐，你可以通过分类筛选或关键词搜索直接抵达。
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 self-start md:gap-4">
              <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                <p className="font-heading text-3xl font-bold text-foreground-950">18</p>
                <p className="mt-1 font-label text-xs text-foreground-500">收录示例影片</p>
              </div>
              <div className="rounded-lg border border-accent-500/40 bg-accent-500/10 p-5">
                <p className="font-heading text-3xl font-bold text-accent-500">18+</p>
                <p className="mt-1 font-label text-xs text-foreground-600">仅限成年用户浏览</p>
              </div>
              <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                <p className="font-heading text-3xl font-bold text-foreground-950">6</p>
                <p className="mt-1 font-label text-xs text-foreground-500">主题分类片单</p>
              </div>
              <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                <p className="font-heading text-3xl font-bold text-foreground-950">0</p>
                <p className="mt-1 font-label text-xs text-foreground-500">播放与下载入口</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16 lg:px-12">
        <div className="flex items-center gap-3">
          <span className="h-5 w-1 rounded-full bg-accent-500" />
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
        </div>
        <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-3">
          {scopeList.map((item, index) => (
            <div
              key={item.title}
              className="animate-rise rounded-lg border border-background-300 bg-background-100 p-6"
              style={{ animationDelay: `${index * 80}ms` }}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                <i className={`${item.icon} text-lg`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full border-y border-background-300 bg-background-100">
        <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-10 px-4 py-14 md:px-8 md:py-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-12">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">内容边界与使用提示</h2>
            </div>
            <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600">
              为了保持站点定位清晰，以下内容我们明确不做。请在使用前了解这些边界：
            </p>
            <ul className="mt-5 space-y-3">
              {limits.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <i className="ri-close-circle-line mt-0.5 text-sm text-foreground-500" />
                  <span className="font-body text-sm leading-relaxed text-foreground-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-lg border border-accent-500/40 bg-accent-500/10 p-4">
              <p className="font-label text-xs leading-relaxed text-foreground-700">
                本站面向成年用户。若你未满 18 岁，请立即离开。浏览时请遵守所在地法律法规与公序良俗。
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">意见反馈</h2>
            </div>
            <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
              发现资料错误、剧情描述有误，或者想推荐某个题材，都可以在这里告诉我们。
            </p>

            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 box-border w-full rounded-lg border border-background-300 bg-background-50 p-5 md:p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="feedback-name" className="mb-2 block font-label text-xs text-foreground-600">
                    称呼 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="box-border h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="feedback-email" className="mb-2 block font-label text-xs text-foreground-600">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="you@example.com"
                    className="box-border h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-4 min-w-0">
                <label htmlFor="feedback-topic" className="mb-2 block font-label text-xs text-foreground-600">
                  反馈类型
                </label>
                <div className="relative">
                  <select
                    id="feedback-topic"
                    name="topic"
                    defaultValue="内容纠错"
                    className="box-border h-11 w-full appearance-none rounded-md border border-background-300 bg-background-100 px-3 pr-9 font-label text-sm text-foreground-900 focus:border-accent-500 focus:outline-none"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="想看某部片">想看某部片</option>
                    <option value="分类建议">分类建议</option>
                    <option value="使用问题">使用问题</option>
                    <option value="其他">其他</option>
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
                </div>
              </div>

              <div className="mt-4 min-w-0">
                <label htmlFor="feedback-message" className="mb-2 block font-label text-xs text-foreground-600">
                  具体内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  onChange={(event) => setCount(event.target.value.length)}
                  placeholder="最多 500 字"
                  className="box-border w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                />
                <div className="mt-1 flex justify-end">
                  <span className="font-label text-[11px] text-foreground-500">{count} / 500</span>
                </div>
              </div>

              <div className="honeypot-guard" aria-hidden="true">
                <input
                  id="feedback-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-label text-[11px] leading-relaxed text-foreground-500">
                  提交即表示你同意我们仅将信息用于内容改进。
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="box-border flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
              </div>

              {feedback && (
                <div
                  role="status"
                  className={`mt-4 flex items-start gap-2 rounded-md border p-3 ${
                    status === "error"
                      ? "border-accent-500/50 bg-accent-500/10"
                      : "border-background-300 bg-background-100"
                  }`}
                >
                  <i
                    className={`mt-0.5 text-sm ${
                      status === "error" ? "ri-error-warning-line text-accent-500" : "ri-checkbox-circle-line text-accent-500"
                    }`}
                  />
                  <p className="min-w-0 font-label text-xs leading-relaxed text-foreground-700">{feedback}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16 lg:px-12">
        <div className="flex items-center gap-3">
          <span className="h-5 w-1 rounded-full bg-accent-500" />
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">法律声明</h2>
        </div>
        <div className="mt-6 rounded-lg border border-background-300 bg-background-100 p-6">
          <p className="font-body text-sm leading-[1.9] text-foreground-700">
            本站为影视资料演示站点，所有影片名称、剧情文本、导演与演员信息均为虚构示例内容，不指向任何真实作品、真实人物或真实机构。
            站内不存储、不上传、不分发任何影片文件，也不提供任何形式的在线播放、下载或付费服务。
          </p>
          <p className="mt-4 font-body text-sm leading-[1.9] text-foreground-600">
            站内涉及的分类、筛选与推荐结果均基于示例数据生成，仅用于展示资料站的信息组织方式。如你对内容有异议，可通过上方反馈表单联系。
            浏览本站即表示你已年满 18 岁，并同意遵守所在地法律法规。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}